<template>
  <div class="visual-report-chart-panel">
    <div class="card shadow-sm">
      <div class="card-header bg-light d-flex align-items-center justify-content-between">
        <h6 class="mb-0 text-primary fw-bold">
          <i :class="chartIcon" class="me-2"></i>
          {{ chart.title || 'Wykres' }}
        </h6>
      </div>

      <div class="card-body">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
          <p class="mt-2 text-muted small">Ładowanie danych wykresu...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="alert alert-warning py-2 mb-0">
          <i class="fas fa-exclamation-triangle me-2"></i>{{ error }}
        </div>

        <!-- No data -->
        <div v-else-if="!hasData" class="text-center py-3 text-muted">
          <i class="fas fa-chart-bar fa-2x mb-2"></i>
          <p class="mb-0 small">Brak danych do wyświetlenia</p>
        </div>

        <!-- Chart -->
        <apexchart
            v-else
            class="apex-charts"
            dir="ltr"
            :type="apexType"
            :height="chartHeight"
            :options="apexOptions"
            :series="apexSeries"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VisualReportChartPanel",
  props: {
    chart: {
      type: Object,
      required: true,
    },
    chartData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  },
  computed: {
    chartIcon() {
      switch (this.safeChart.chartType) {
        case "line":
          return "fas fa-chart-line";
        case "pie":
          return "fas fa-chart-pie";
        case "bar":
        default:
          return "fas fa-chart-bar";
      }
    },
    safeChart() {
      return {
        chartType: this.chart?.chartType || "bar",
        title: this.chart?.title || "Chart",
        visible: this.chart?.visible !== false,
        stacked: this.chart?.stacked === true,
        chartSettings: {
          width: this.chart?.chartSettings?.width || "100%",
          height: this.chart?.chartSettings?.height || 360,
          showLegend: this.chart?.chartSettings?.showLegend !== false,
          showDataLabels: this.chart?.chartSettings?.showDataLabels === true,
          showToolbar: this.chart?.chartSettings?.showToolbar === true,
        },
      };
    },

    safeBarLineSeries() {
      const rawSeries = Array.isArray(this.chartData?.series) ? this.chartData.series : [];
      return rawSeries
          .filter(series => series && Array.isArray(series.data))
          .map(series => ({
            name: series.name || "Series",
            data: Array.isArray(series.data) ? series.data : [],
          }));
    },

    safePieSeries() {
      const rawSeries = Array.isArray(this.chartData?.series) ? this.chartData.series : [];
      // Handle legacy format: array of numbers
      if (rawSeries.length > 0 && typeof rawSeries[0] === "number") {
        return rawSeries;
      }
      // Backend format: array of {name, data} — use first series data array
      if (rawSeries.length > 0 && rawSeries[0] && Array.isArray(rawSeries[0].data)) {
        return rawSeries[0].data.map((v) => (typeof v === "number" ? v : 0));
      }
      return [];
    },

    safeCategories() {
      const categories = this.chartData?.categories;
      return Array.isArray(categories) ? categories : [];
    },

    safeLabels() {
      // Prefer explicit labels if present, otherwise fall back to categories (backend pie format)
      const labels = this.chartData?.labels;
      if (Array.isArray(labels) && labels.length > 0) return labels;
      const categories = this.chartData?.categories;
      return Array.isArray(categories) ? categories : [];
    },

    hasData() {
      if (this.safeChart.chartType === "pie") {
        return this.safePieSeries.length > 0 && this.safeLabels.length > 0;
      }

      return (
          this.safeBarLineSeries.length > 0 &&
          this.safeCategories.length > 0 &&
          this.safeBarLineSeries.some(series => Array.isArray(series.data) && series.data.length > 0)
      );
    },

    chartHeight() {
      return this.safeChart.chartSettings.height || 360;
    },

    apexType() {
      const type = this.safeChart.chartType;
      if (type === "bar" || type === "line" || type === "pie") {
        return type;
      }
      return "bar";
    },

    apexSeries() {
      let result;

      if (this.safeChart.chartType === "pie") {
        result = this.safePieSeries;
      } else {
        result = this.safeBarLineSeries;
      }

      return JSON.parse(JSON.stringify(result));
    },

    apexOptions() {
      const baseOptions = {
        chart: {
          type: this.apexType,
          height: this.chartHeight,
          toolbar: {
            show: this.safeChart.chartSettings.showToolbar,
          },
        },
        title: {
          text: this.safeChart.title,
          align: "left",
        },
        legend: {
          show: this.safeChart.chartSettings.showLegend,
        },
        dataLabels: {
          enabled: this.safeChart.chartSettings.showDataLabels,
        },
        noData: {
          text: "No data",
        },
        colors: [
          "#3b82f6",
          "#10b981",
          "#f59e0b",
          "#ef4444",
          "#8b5cf6",
          "#06b6d4",
        ],
      };

      if (this.safeChart.chartType === "pie") {
        baseOptions.labels = this.safeLabels;
      } else {
        baseOptions.xaxis = {
          categories: this.safeCategories,
        };
      }

      if (this.safeChart.chartType === "line") {
        baseOptions.stroke = {
          curve: "smooth",
          width: 2,
        };
      }

      if (this.safeChart.chartType === "bar") {
        baseOptions.plotOptions = {
          bar: {
            horizontal: false,
            columnWidth: "40%",
            borderRadius: 3,
          },
        };
        baseOptions.chart.stacked = this.safeChart.stacked;
      }

      return JSON.parse(JSON.stringify(baseOptions));
    },
  }
};
</script>
